import { IconProp } from '@fortawesome/fontawesome-svg-core';
import HlIcon, { HlIconProps } from '@hl-components/Icon/Icon';
import clsx from 'clsx';
import React, { MouseEvent } from 'react';
import './IconButton.scss';

type HlIconButtonStyle = 'filled' | 'outlined' | 'tertiary';
type HlIconButtonColor =
  | 'primary'
  | 'subtle'
  | 'negative'
  | 'warning'
  | 'info'
  | 'positive'
  | 'contrast';

export interface HlIconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconProp;
  size: 'xsmall' | 'small' | 'medium' | 'large';
  styleVariant?: HlIconButtonStyle;
  color?: HlIconButtonColor;
  disabled?: boolean;
  /**
   * Ignores clicks and shows a wait cursor, e.g. while a download runs. Uses
   * aria-disabled rather than `disabled`, so focus and hover events are kept.
   */
  isLoading?: boolean;
  onClick?: (event: MouseEvent) => void;
  'aria-label'?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  tabIndex?: number;
}

const iconSizeMap: Record<HlIconButtonProps['size'], HlIconProps['size']> = {
  xsmall: 'xxsmall',
  small: 'xsmall',
  medium: 'small',
  large: 'medium'
};

export const HlIconButton = ({
  icon,
  size,
  styleVariant = 'filled',
  color = 'primary',
  disabled = false,
  isLoading,
  'aria-label': ariaLabel,
  onClick = () => {},
  className,
  type = 'button',
  tabIndex,
  ...buttonProps
}: HlIconButtonProps) => {
  return (
    <button
      className={clsx('hl-icon-button', className, {
        'is-loading': isLoading,
        'hl-filled': styleVariant === 'filled',
        'hl-outlined': styleVariant === 'outlined',
        'hl-tertiary': styleVariant === 'tertiary',

        'hl-primary': color === 'primary',
        'hl-subtle': color === 'subtle',
        'hl-negative': color === 'negative',
        'hl-warning': color === 'warning',
        'hl-info': color === 'info',
        'hl-positive': color === 'positive',
        'hl-contrast': color === 'contrast',

        'is-xsmall-sized hl-corners-xs': size === 'xsmall',
        'is-small-sized hl-corners-md': size === 'small',
        'is-medium-sized hl-corners-md': size === 'medium',
        'is-large-sized hl-corners-lg': size === 'large'
      })}
      aria-label={ariaLabel}
      disabled={disabled}
      aria-disabled={isLoading || undefined}
      aria-busy={isLoading || undefined}
      onClick={isLoading ? (event) => event.preventDefault() : onClick}
      type={type}
      tabIndex={tabIndex}
      {...buttonProps}
    >
      <HlIcon icon={icon} size={iconSizeMap[size]} />
    </button>
  );
};

export default HlIconButton;
